import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { USER_ROLES } from "@/constants/auth";
import { useFormError } from "@/hooks/useFormError";
const ADD_USER_ROLES = [
    USER_ROLES.USER,
    USER_ROLES.VIEWER,
    USER_ROLES.ADMIN
];
// Validation schema
const addUserSchema = z.object({
    email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
    fullName: z.string().min(1, "Full name is required").min(2, "Full name must be at least 2 characters"),
    role: z.enum(ADD_USER_ROLES).default(USER_ROLES.USER)
});
export const AddUserModal = ({ isOpen, onClose, onSubmit, isLoading })=>{
    const { register, handleSubmit, formState: { isSubmitting, isValid }, reset, setError } = useForm({
        resolver: zodResolver(addUserSchema),
        mode: "onChange",
        defaultValues: {
            email: "",
            fullName: "",
            role: USER_ROLES.USER
        }
    });
    const handleFormError = useFormError();
    const onFormSubmit = async (data)=>{
        try {
            await onSubmit(data);
            reset();
            onClose();
        } catch (error) {
            handleFormError(error, setError);
        }
    };
    const handleClose = ()=>{
        reset();
        onClose();
    };
    if (!isOpen) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50",
        children: /*#__PURE__*/ _jsx("div", {
            className: "relative top-1/2 transform -translate-y-1/2 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "mt-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between mb-4",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "text-lg font-medium text-gray-900",
                                children: "Add New User"
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                onClick: handleClose,
                                className: "text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed",
                                disabled: isLoading || isSubmitting,
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "sr-only",
                                        children: "Close"
                                    }),
                                    /*#__PURE__*/ _jsx("svg", {
                                        className: "h-6 w-6",
                                        fill: "none",
                                        viewBox: "0 0 24 24",
                                        stroke: "currentColor",
                                        children: /*#__PURE__*/ _jsx("path", {
                                            strokeLinecap: "round",
                                            strokeLinejoin: "round",
                                            strokeWidth: 2,
                                            d: "M6 18L18 6M6 6l12 12"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("form", {
                        onSubmit: handleSubmit(onFormSubmit),
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "mb-4",
                                children: /*#__PURE__*/ _jsx(Input, {
                                    label: "Email *",
                                    type: "email",
                                    placeholder: "user@example.com",
                                    ...register("email")
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mb-4",
                                children: /*#__PURE__*/ _jsx(Input, {
                                    label: "Full Name *",
                                    type: "text",
                                    placeholder: "John Doe",
                                    ...register("fullName")
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mb-4",
                                children: /*#__PURE__*/ _jsxs(Select, {
                                    label: "Role",
                                    id: "role",
                                    ...register("role"),
                                    disabled: isLoading || isSubmitting,
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: USER_ROLES.USER,
                                            children: "User"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: USER_ROLES.VIEWER,
                                            children: "Viewer"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: USER_ROLES.ADMIN,
                                            children: "Admin"
                                        })
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-end space-x-3 pt-4",
                                children: [
                                    /*#__PURE__*/ _jsx(Button, {
                                        type: "button",
                                        variant: "simple",
                                        buttonStyle: "flat",
                                        onClick: handleClose,
                                        disabled: isLoading || isSubmitting,
                                        children: "Cancel"
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        type: "submit",
                                        variant: "simple",
                                        color: "navy-90",
                                        disabled: isLoading || isSubmitting || !isValid,
                                        children: isLoading || isSubmitting ? "Adding..." : "Add User"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        })
    });
};
